/** Graph canvas helper functions — element conversion, focus mode, clear */

import type { Core } from 'cytoscape'
import type { GraphResponse } from '@agentwiki/shared'

/** Convert GraphResponse to Cytoscape element definitions */
export function toElements(data: GraphResponse) {
  return [
    ...data.nodes.map((n) => ({
      group: 'nodes' as const,
      data: {
        id: n.data.id,
        label: n.data.label,
        category: n.data.category ?? 'uncategorized',
        degree: n.data.degree ?? 0,
        summary: n.data.summary ?? '',
        tags: (n.data.tags ?? []).join(', '),
      },
    })),
    ...data.edges.map((e) => ({
      group: 'edges' as const,
      data: {
        id: e.data.id,
        source: e.data.source,
        target: e.data.target,
        type: e.data.type,
        implicit: e.data.implicit,
        weight: e.data.weight,
        score: e.data.score,
        context: e.data.context ?? '',
      },
    })),
  ]
}

/** Apply focus mode — dim all nodes/edges except selected and its neighbors */
export function applyFocusMode(cy: Core, selectedNodeId: string) {
  const selectedNode = cy.getElementById(selectedNodeId)
  if (!selectedNode.length) return

  // neighborhood() includes connected edges + neighbor nodes
  const neighborhood = selectedNode.neighborhood().add(selectedNode)

  // Dim everything, then un-dim the neighborhood
  cy.elements().addClass('dimmed')
  neighborhood.removeClass('dimmed')
}

/** Clear focus mode — restore all elements to full opacity */
export function clearFocusMode(cy: Core) {
  cy.elements().removeClass('dimmed')
}

/** Apply hover highlight — lighter dim, doesn't interfere with click focus */
export function applyHoverHighlight(cy: Core, nodeId: string) {
  // Skip if click-focus is active (dimmed class present)
  if (cy.elements('.dimmed').length > 0) return

  const node = cy.getElementById(nodeId)
  if (!node.length) return

  const neighborhood = node.neighborhood().add(node)
  cy.elements().addClass('hover-dimmed')
  neighborhood.removeClass('hover-dimmed').addClass('highlighted')
}

/** Clear hover highlight */
export function clearHoverHighlight(cy: Core) {
  cy.elements().removeClass('hover-dimmed highlighted')
}

/** Apply Level-of-Detail based on current zoom level (batched for performance) */
export function applyLoD(cy: Core) {
  const zoom = cy.zoom()
  const nodeCount = cy.nodes().length
  cy.startBatch()

  // Smart labels (GH-128): hide labels based on zoom + graph density
  // Dense graphs (>60 nodes): hide labels at zoom < 0.8 to reduce clutter
  // Sparse graphs: hide at zoom < 0.5 (original threshold)
  const labelThreshold = nodeCount > 60 ? 0.8 : 0.5
  if (zoom < labelThreshold) {
    cy.nodes().addClass('lod-no-label')
  } else {
    cy.nodes().removeClass('lod-no-label')
  }

  // Reduce edge opacity further when zoomed out — use selectors to avoid per-edge loop
  if (zoom < 0.35) {
    cy.edges('[!implicit]').style('opacity', 0.15)
    cy.edges('[?implicit]').style('opacity', 0.08)
  } else {
    cy.edges().removeStyle('opacity')
  }

  cy.endBatch()
}

/** Apply smart labels on hover — show label for hovered node even when hidden by LoD (GH-128) */
export function showLabelOnHover(cy: Core, nodeId: string) {
  cy.getElementById(nodeId).removeClass('lod-no-label')
}

/** Hide label again after hover leaves (only if LoD would hide it) */
export function hideLabelAfterHover(cy: Core, nodeId: string) {
  const zoom = cy.zoom()
  const nodeCount = cy.nodes().length
  const labelThreshold = nodeCount > 60 ? 0.8 : 0.5
  if (zoom < labelThreshold) {
    cy.getElementById(nodeId).addClass('lod-no-label')
  }
}
